import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, bufferTime, filter } from 'rxjs';
import { LogEntry, LoggingConfig } from '../models/log-entry.model';
import { LogLevel } from '../enums/log-level.enum';
import envConfig from '../../env-config';

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER_SIZE = 1000;

const DEFAULT_LOGGING_CONFIG: LoggingConfig = {
  enabled: true,
  minLevel: LogLevel.Information,
  batchSize: DEFAULT_BATCH_SIZE,
  flushInterval: DEFAULT_FLUSH_INTERVAL_MS,
  endpoint: '/api/logs',
  consoleLogging: true,
  sendToBackend: false,
};

/**
 * Centralized logging service for FPS.Web Angular application
 *
 * Features:
 * - Structured logging with properties
 * - Automatic batching (50 entries or 5 seconds)
 * - Console logging in development
 * - Sends logs to backend API endpoint
 * - Memory-efficient with buffer limits
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private readonly http = inject(HttpClient);
  private readonly logBuffer$ = new Subject<LogEntry>();
  private readonly config: LoggingConfig =
    envConfig.logging ?? DEFAULT_LOGGING_CONFIG;
  private readonly maxBufferSize = MAX_BUFFER_SIZE;
  private bufferCount = 0;
  private pendingLogs: LogEntry[] = [];

  constructor() {
    if (this.config.enabled) {
      this.setupBatchLogging();
      this.setupBeforeUnload();
    }
  }

  /**
   * Log debug message (development only)
   */
  debug(message: string, properties?: Record<string, unknown>): void {
    if (this.config.minLevel <= LogLevel.Debug) {
      this.log(message, LogLevel.Debug, properties);
    }
  }

  /**
   * Log informational message (normal application flow)
   */
  info(message: string, properties?: Record<string, unknown>): void {
    if (this.config.minLevel <= LogLevel.Information) {
      this.log(message, LogLevel.Information, properties);
    }
  }

  /**
   * Log warning message (recoverable issues)
   */
  warn(message: string, properties?: Record<string, unknown>): void {
    if (this.config.minLevel <= LogLevel.Warning) {
      this.log(message, LogLevel.Warning, properties);
    }
  }

  /**
   * Log error message (failures requiring attention)
   */
  error(
    message: string,
    error?: Error,
    properties?: Record<string, unknown>
  ): void {
    const errorProps = {
      ...properties,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
    };
    this.log(message, LogLevel.Error, errorProps);
  }

  /**
   * Manually flush log buffer to backend
   */
  flush(): void {
    if (this.pendingLogs.length > 0) {
      const logsToSend = [...this.pendingLogs];
      this.pendingLogs = [];
      this.sendLogs(logsToSend);
      this.bufferCount = 0;
    }
  }

  private log(
    message: string,
    level: LogLevel,
    properties?: Record<string, unknown>
  ): void {
    const entry: LogEntry = {
      message,
      level,
      timestamp: new Date(),
      properties,
    };

    const isProduction = envConfig.production ?? false;
    if (
      this.config.consoleLogging ||
      (!isProduction && level >= LogLevel.Warning)
    ) {
      this.logToConsole(entry);
    }

    if (this.bufferCount < this.maxBufferSize) {
      this.pendingLogs.push(entry);
      this.logBuffer$.next(entry);
      this.bufferCount++;
    } else {
      console.warn('[LoggingService] Buffer overflow - log entry dropped');
    }
  }

  private logToConsole(entry: LogEntry): void {
    const timestamp = entry.timestamp.toISOString();
    const prefix = `[${timestamp}]`;
    const props = entry.properties ?? '';

    switch (entry.level) {
      case LogLevel.Debug:
        console.debug(prefix, entry.message, props);
        break;
      case LogLevel.Information:
        console.info(prefix, entry.message, props);
        break;
      case LogLevel.Warning:
        console.warn(prefix, entry.message, props);
        break;
      case LogLevel.Error:
        console.error(prefix, entry.message, props);
        break;
    }
  }

  private setupBatchLogging(): void {
    this.logBuffer$
      .pipe(
        bufferTime(this.config.flushInterval, undefined, this.config.batchSize),
        filter(logs => logs.length > 0)
      )
      .subscribe({
        next: logs => {
          // Remove these logs from pending array
          this.pendingLogs = this.pendingLogs.filter(
            log => !logs.includes(log)
          );
          this.sendLogs(logs);
          this.bufferCount = Math.max(0, this.bufferCount - logs.length);
        },
        error: err => console.error('[LoggingService] Buffer error:', err),
      });
  }

  private sendLogs(logs: LogEntry[]): void {
    // Skip if sending to backend is disabled
    if (this.config.sendToBackend === false) {
      return;
    }

    const apiUrl = envConfig.apiUrl ?? '';
    const endpoint = `${apiUrl}${this.config.endpoint}`;

    this.http
      .post(endpoint, logs, {
        headers: {
          'X-Suppress-Correlator': 'true',
        },
      })
      .subscribe({
        error: err => {
          console.error(
            '[LoggingService] Failed to send logs to backend:',
            err
          );
        },
      });
  }

  private setupBeforeUnload(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }
}
